// Theme detection for the task pane. Outlook exposes the host theme via
// Office.context.officeTheme (Mailbox requirement set 1.14+). Note: the
// `isDarkTheme` flag is NOT supported in Outlook, so we derive dark/light from
// the luminance of bodyBackgroundColor. When officeTheme is unavailable (older
// host, or Office not ready yet) we fall back to the OS `prefers-color-scheme`.

export type ThemeMode = 'light' | 'dark';

export interface OfficeThemeLike {
  bodyBackgroundColor?: string;
  isDarkTheme?: boolean;
}

// true = dark, false = light, null = couldn't parse.
function hexIsDark(hex: string | undefined): boolean | null {
  if (!hex) return null;
  const digits = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim())?.[1];
  if (!digits) return null;
  const int = parseInt(digits, 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  // Perceived luminance (sRGB weights). Below the midpoint reads as a dark UI.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

export function themeModeFromOfficeTheme(
  theme: OfficeThemeLike | undefined | null,
): ThemeMode | null {
  if (!theme) return null;
  // Honor isDarkTheme where the host provides it (non-Outlook hosts); Outlook
  // leaves it undefined, so fall through to the background-color heuristic.
  if (typeof theme.isDarkTheme === 'boolean') return theme.isDarkTheme ? 'dark' : 'light';
  const dark = hexIsDark(theme.bodyBackgroundColor);
  return dark === null ? null : dark ? 'dark' : 'light';
}

export function currentOfficeTheme(): OfficeThemeLike | null {
  const office = (globalThis as { Office?: typeof Office }).Office;
  return (office?.context?.officeTheme as OfficeThemeLike | undefined) ?? null;
}

export function prefersDarkMode(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

// Best available reading right now: Office theme first, OS preference otherwise.
export function resolveThemeMode(theme?: OfficeThemeLike | null): ThemeMode {
  return (
    themeModeFromOfficeTheme(theme ?? currentOfficeTheme()) ??
    (prefersDarkMode() ? 'dark' : 'light')
  );
}
